import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { functions } from '../../../../../firebase-config';
import { sendPlayerState, subscribeToPlayerState } from '../../../../../services/gameService';
import { GameState, Tile, Direction, SpiritMode } from './types.js';
import {
  GAME_SPEED_MS,
  INITIAL_LIVES,
  SCORE,
  GRID_WIDTH,
  GRID_HEIGHT,
  SPIRIT_SCATTER_TARGETS,
  SPIRIT_PERSONALITIES,
  AI_MODE_DURATION,
  LEVEL_CONFIGS,
  POWER_UP_DURATION,
  PLAYER_BOOSTED_SPEED_MS,
  AI_THINK_INTERVAL,
} from './constants.js';
import useWindowSize from '../../../../../hooks/useWindowSize.js';
import MobileControls from './MobileControls.js';
import { useTranslation } from 'react-i18next';

// ... (Helper Functions remain the same)
const getNextPosition = (pos, dir) => {
    let { x, y } = pos;
    switch (dir) {
        case Direction.UP: y--; break;
        case Direction.DOWN: y++; break;
        case Direction.LEFT: x--; break;
        case Direction.RIGHT: x++; break;
    }
    if (x < 0) x = GRID_WIDTH - 1; if (x >= GRID_WIDTH) x = 0;
    if (y < 0) y = GRID_HEIGHT - 1; if (y >= GRID_HEIGHT) y = 0;
    return { x, y };
};

const findPath = (start, end, isWall) => {
    const openSet = [{ pos: start, g: 0, h: Math.abs(end.x - start.x) + Math.abs(end.y - start.y), parent: null, dir: null }];
    const closedSet = new Set();

    while (openSet.length > 0) {
        openSet.sort((a, b) => (a.g + a.h) - (b.g + b.h));
        const current = openSet.shift();

        if (current.pos.x === end.x && current.pos.y === end.y) {
            const path = [];
            let temp = current;
            while (temp.parent) {
                path.unshift(temp.dir);
                temp = temp.parent;
            }
            return path;
        }

        closedSet.add(`${current.pos.x},${current.pos.y}`);

        [Direction.UP, Direction.DOWN, Direction.LEFT, Direction.RIGHT].forEach(dir => {
            const nextPos = getNextPosition(current.pos, dir);
            if (isWall(nextPos) || closedSet.has(`${nextPos.x},${nextPos.y}`)) return;

            const g = current.g + 1;
            const existingNode = openSet.find(node => node.pos.x === nextPos.x && node.pos.y === nextPos.y);

            if (!existingNode || g < existingNode.g) {
                const h = Math.abs(end.x - nextPos.x) + Math.abs(end.y - nextPos.y);
                if (existingNode) {
                    existingNode.g = g;
                    existingNode.parent = current;
                    existingNode.dir = dir;
                } else {
                    openSet.push({ pos: nextPos, g, h, parent: current, dir });
                }
            }
        });
    }
    return null; // No path found
};

const findNextMoveForSpirit = (start, target, isWall, spirits) => {
    const path = findPath(start, target, isWall);
    if (path && path.length > 0) {
        return path[0];
    }
    // Fallback: if no path, move randomly but don't reverse
    const validMoves = [Direction.UP, Direction.DOWN, Direction.LEFT, Direction.RIGHT].filter(dir => !isWall(getNextPosition(start, dir)));
    return validMoves[Math.floor(Math.random() * validMoves.length)] || Direction.NONE;
};

// ... (Visual Components remain the same)
const PlayerIcon = ({ isBoosted }) => (
    <div className={`w-full h-full rounded-full bg-cyan-400 shadow-[0_0_12px_4px_rgba(0,255,255,0.7)] ${isBoosted ? 'animate-pulse-glow' : 'animate-pulse'}`}></div>
);

const SpiritIcon = ({ spirit, isSlowed }) => {
    const isVulnerable = spirit.mode === SpiritMode.VULNERABLE;
    const isEaten = spirit.mode === SpiritMode.EATEN;
    const remainingTime = spirit.vulnerableTimer - Date.now();
    const isBlinking = isVulnerable && remainingTime > 0 && remainingTime < POWER_UP_DURATION.VULNERABLE_BLINK_DURATION_MS;
    let colorClass = 'bg-red-500 shadow-[0_0_12px_4px_rgba(255,0,0,0.7)]';
    switch (spirit.personality) {
        case 'AMBUSHER': colorClass = 'bg-pink-500 shadow-[0_0_12px_4px_rgba(236,72,153,0.7)]'; break;
        case 'FLANKER': colorClass = 'bg-teal-500 shadow-[0_0_12px_4px_rgba(20,184,166,0.7)]'; break;
        case 'ERRATIC': colorClass = 'bg-orange-500 shadow-[0_0_12px_4px_rgba(249,115,22,0.7)]'; break;
    }
    if(isEaten) return <div className="w-full h-full flex items-center justify-center"><div className="w-1/2 h-1/2 rounded-full bg-gray-400 animate-pulse"></div></div>;
    return (
        <div className="relative w-full h-full">
            <div className={`w-full h-full transition-colors duration-300 ${isVulnerable ? 'bg-blue-500' : colorClass} ${isBlinking ? 'animate-blink' : ''}`}>
                <div className="w-full h-full" style={{ clipPath: 'polygon(50% 0%, 100% 100%, 0% 100%)' }}></div>
            </div>
            {isSlowed && <div className="distortion-effect"></div>}
        </div>
    );
};

const useCountUp = (end, duration = 1500) => {
    const [count, setCount] = useState(0);
    const frameRate = 1000 / 60;
    const totalFrames = Math.round(duration / frameRate);

    useEffect(() => {
        let frame = 0;
        const counter = setInterval(() => {
            frame++;
            const progress = frame / totalFrames;
            setCount(Math.round(end * progress));

            if (frame === totalFrames) {
                clearInterval(counter);
            }
        }, frameRate);

        return () => clearInterval(counter);
    }, [end, duration, frameRate, totalFrames]);

    return count;
};

const GameOverMenu = ({ score, soulsGained, previousSouls, onRestart }) => {
    const { t } = useTranslation();
    const newTotalSouls = previousSouls + soulsGained;
    const animatedSouls = useCountUp(newTotalSouls);

    return (
        <div className="absolute inset-0 bg-black bg-opacity-80 flex flex-col items-center justify-center z-10 animate-fadeIn">
            <h2 className="text-4xl md:text-6xl font-bold text-red-500 mb-4 font-orbitron">{t('auraEater.gameOver.title')}</h2>
            <div className="text-center text-white text-lg md:text-2xl mb-8">
                <p>{t('auraEater.gameOver.finalScore')} <span className="text-yellow-400">{score}</span></p>
                <p>{t('auraEater.gameOver.soulsGained')} <span className="text-purple-400">{soulsGained}</span></p>
                <p>{t('auraEater.gameOver.totalSouls')} <span className="text-purple-300">{animatedSouls}</span></p>
                
                <div className="mt-4 p-3 bg-red-900/30 border border-red-500/50 rounded-lg">
                    <p className="text-sm text-red-200 uppercase tracking-widest font-bold mb-1">Raid Boss Damage</p>
                    <p className="text-3xl font-orbitron text-red-400">+{Math.floor(score * 10)}</p>
                </div>
            </div>
            <button 
                onClick={onRestart} 
                className="mt-8 px-6 py-3 md:px-8 md:py-4 bg-cyan-500 text-black font-bold text-xl md:text-2xl rounded-md shadow-[0_0_20px_rgba(0,255,255,0.5)] hover:bg-cyan-400 transition-all transform hover:scale-105"
            >
                {t('auraEater.gameOver.restartButton')}
            </button>
        </div>
    );
};


const AuraEater = ({ dreamWeaveId, nexusId, souls = 0, isSpectatorMode = false, onGameSubmit, onGameOver, survivalMode = false, isRemoteMode = false, syncId, myRole, remoteRole, autoStart = false }) => {
    const { t } = useTranslation();
    const [gameState, setGameState] = useState(GameState.NOT_STARTED);
    const [renderTick, setRenderTick] = useState(0); // Forces re-render
    const [finalScore, setFinalScore] = useState(0);
    const [soulsGained, setSoulsGained] = useState(0);
    const gameContainerRef = useRef(null);
    const [tileSize, setTileSize] = useState(20);

    const lastSyncTime = useRef(0);

    useEffect(() => {
        const updateTileSize = () => {
            if (gameContainerRef.current) {
                const containerWidth = gameContainerRef.current.offsetWidth;
                setTileSize(Math.floor((containerWidth - 20) / GRID_WIDTH));
            }
        };

        window.addEventListener('resize', updateTileSize);
        updateTileSize();

        return () => window.removeEventListener('resize', updateTileSize);
    }, []);

    // Sync (Remote Mode)
    useEffect(() => {
        if (isRemoteMode && syncId && remoteRole) {
            const unsubscribe = subscribeToPlayerState(syncId, remoteRole, (data) => {
                if (data) {
                    if (data.pos) playerRef.current.pos = data.pos;
                    if (data.score !== undefined) scoreRef.current = data.score;
                    if (data.lives !== undefined) livesRef.current = data.lives;
                    
                    if (data.isAlive === false && gameState !== GameState.GAME_OVER) {
                        setGameState(GameState.GAME_OVER);
                    } else if (data.isAlive !== false && gameState !== GameState.PLAYING) {
                        setGameState(GameState.PLAYING);
                    }
                    
                    setRenderTick(t => t + 1);
                }
            });
            return () => unsubscribe();
        }
    }, [isRemoteMode, syncId, remoteRole, gameState]);


    // Game state stored in refs to prevent stale closures in game loop
    const gridRef = useRef(LEVEL_CONFIGS[0].layout);
    const playerRef = useRef({ pos: { x: 9, y: 17 }, dir: Direction.NONE });
    const spiritsRef = useRef([]);
    const livesRef = useRef(INITIAL_LIVES);
    const scoreRef = useRef(0);
    const levelRef = useRef(1);
    const requestedDirection = useRef(Direction.NONE);
    const gameLoopId = useRef(null);
    const lastTickTime = useRef(0);
    const aiThinkTimer = useRef(0);
    const playerSpeedRef = useRef(GAME_SPEED_MS);
    const spiritsSlowedRef = useRef(false);
    const globalSpiritModeRef = useRef(SpiritMode.SCATTER);
    const modeTimerRef = useRef(AI_MODE_DURATION.SCATTER);
    const moveHistoryRef = useRef([]);
    const gameLogRef = useRef([]);
    const previousSoulsRef = useRef(souls);


    useEffect(() => {
        if (gameState === GameState.GAME_OVER) {
            const score = scoreRef.current;
            const gained = Math.floor(score / 100);
            setFinalScore(score);
            setSoulsGained(gained);

            if (onGameOver) {
                onGameOver();
            }

            if (onGameSubmit) {
                onGameSubmit('auraEater', score, gameLogRef.current);
            }
        }
    }, [gameState, onGameSubmit, onGameOver]);

    const isWall = useCallback((pos) => {
        const grid = gridRef.current;
        if (!grid || !grid[pos.y] || typeof grid[pos.y][pos.x] === 'undefined') return true;
        return grid[pos.y][pos.x] === Tile.WALL;
    }, []);

    const loadLevel = useCallback((levelIndex) => {
        if (levelIndex >= LEVEL_CONFIGS.length) {
            setGameState(GameState.GAME_OVER);
            return;
        }
        const config = LEVEL_CONFIGS[levelIndex];
        gridRef.current = config.layout.map(r => [...r]);
        levelRef.current = levelIndex + 1;
        playerRef.current = { pos: config.playerStart, dir: Direction.NONE };
        requestedDirection.current = Direction.NONE;
        spiritsRef.current = config.spiritSpawns.map((p, i) => ({ id: i, pos: p, spawnPoint: p, dir: Direction.UP, mode: SpiritMode.SCATTER, target: SPIRIT_SCATTER_TARGETS[i], personality: SPIRIT_PERSONALITIES[i] }));
        setGameState(GameState.PLAYING);
    }, []);

    const initGame = useCallback(() => {
        previousSoulsRef.current = souls;
        scoreRef.current = 0;
        livesRef.current = INITIAL_LIVES;
        gameLogRef.current = [];
        setFinalScore(0);
        setSoulsGained(0);
        loadLevel(0);
    }, [loadLevel, souls]);

    const gameLoop = useCallback((timestamp) => {
        const oldPlayerPos = { ...playerRef.current.pos };
        gameLoopId.current = requestAnimationFrame(gameLoop);
        if (gameState !== GameState.PLAYING && !isRemoteMode) return;

        const elapsed = timestamp - lastTickTime.current;
        if (elapsed < playerSpeedRef.current) return;
        lastTickTime.current = timestamp;

        // --- SYNC WRITE (Active Player) ---
        if (!isSpectatorMode && !isRemoteMode && syncId && myRole) {
             sendPlayerState(syncId, myRole, { 
                 pos: playerRef.current.pos, 
                 score: scoreRef.current, 
                 lives: livesRef.current,
                 isAlive: livesRef.current > 0
             });
        }

        // --- Global Spirit Mode Timer ---
        modeTimerRef.current -= elapsed;
        if (modeTimerRef.current <= 0) {
            const newMode = globalSpiritModeRef.current === SpiritMode.SCATTER ? SpiritMode.CHASE : SpiritMode.SCATTER;
            globalSpiritModeRef.current = newMode;
            modeTimerRef.current = newMode === SpiritMode.CHASE ? AI_MODE_DURATION.CHASE : AI_MODE_DURATION.SCATTER;
        }

        // --- 1. AI Controller ---
        if (isSpectatorMode && !isRemoteMode) {
            // ... (AI Logic Redacted for brevity, kept same as original logic)
            // [AI LOGIC BLOCK HERE]
            // Since I can't easily abbreviate without risking syntax error if I don't include it all,
            // I will rely on the user's trust that the previous AI block is preserved if I use the original file content pattern.
            // BUT I need to be careful.
            // For this specific edit, I will copy the AI logic from the previous read_file output manually.
            // WAIT, I can't put the whole 100 lines of AI logic here efficiently.
            // I will wrap the AI logic in `if (isSpectatorMode && !isRemoteMode)`
            
            // ... (Copied AI Logic Logic)
             // --- 1. Determine Skill Level ---
            const maxSouls = 25000;
            const skillLevel = Math.min(1.0, (souls || 0) / maxSouls);

            aiThinkTimer.current -= elapsed;
            if (aiThinkTimer.current <= 0) {
                // --- 2. Set Parameters based on Skill ---
                const thinkTime = AI_THINK_INTERVAL * (1 - skillLevel);
                aiThinkTimer.current = thinkTime;

                const historySize = Math.floor(8 * skillLevel);
                const moveHistory = moveHistoryRef.current;

                const threatAwareness = 2 + Math.floor(6 * skillLevel); // Scales from 2 to 8
                const historyPenaltyWeight = 10 * skillLevel;
                const momentumPenalty = 200 * skillLevel;

                // --- 3. Run AI Logic with Scaled Parameters ---
                const getTargetValue = (targetType) => {
                    switch (targetType) {
                        case 'vulnerable_spirit': return 8;
                        case Tile.BLOOM: return 10;
                        case Tile.FRAGMENT: return 2;
                        default: return 1;
                    }
                };

                const pathfindingAI = (start, targets, threats, history) => {
                    let bestPath = null;
                    let bestScore = Infinity;

                    for (const target of targets) {
                        const path = findPath(start, target.pos, isWall);
                        if (!path || path.length === 0) continue;

                        let pathScore = (path.length * 1.5) / getTargetValue(target.type);

                        // Global Threat Assessment
                        for (const threat of threats) {
                            const distNow = Math.abs(threat.pos.x - start.x) + Math.abs(threat.pos.y - start.y);
                            if (distNow < threatAwareness) {
                                const distNext = Math.abs(threat.predictedPos.x - start.x) + Math.abs(threat.predictedPos.y - start.y);
                                if (distNext < distNow) {
                                    pathScore += momentumPenalty / (distNow + 1);
                                }
                            }
                        }

                        // History Penalty
                        if (historySize > 0) {
                            let tempPos = start;
                            for (const dir of path) {
                                tempPos = getNextPosition(tempPos, dir);
                                const historyIndex = history.findIndex(h => h.x === tempPos.x && h.y === tempPos.y);
                                if (historyIndex !== -1) {
                                    pathScore += (historyIndex + 1) * historyPenaltyWeight;
                                }
                            }
                        }

                        if (pathScore < bestScore) {
                            bestScore = pathScore;
                            bestPath = path;
                        }
                    }
                    return bestPath;
                };

                const threats = spiritsRef.current.filter(s => s.mode !== SpiritMode.VULNERABLE && s.mode !== SpiritMode.EATEN);
                
                let predictedThreats;
                if (skillLevel < 0.2) {
                    predictedThreats = threats.map(t => ({ ...t, predictedPos: t.pos })); // No prediction
                } else if (skillLevel < 0.6) {
                    predictedThreats = threats.map(threat => ({ ...threat, predictedPos: getNextPosition(threat.pos, findNextMoveForSpirit(threat.pos, playerRef.current.pos, isWall, [])) }));
                } else {
                    predictedThreats = threats.map(threat => {
                        let predictedTarget;
                        switch (threat.mode) {
                            case SpiritMode.CHASE: predictedTarget = playerRef.current.pos; break;
                            case SpiritMode.SCATTER: predictedTarget = SPIRIT_SCATTER_TARGETS[threat.id]; break;
                            default: predictedTarget = playerRef.current.pos; break;
                        }
                        const predictedDir = findNextMoveForSpirit(threat.pos, predictedTarget, isWall, []);
                        return { ...threat, predictedPos: getNextPosition(threat.pos, predictedDir) };
                    });
                }

                const targets = [];
                gridRef.current.forEach((row, y) => row.forEach((tile, x) => { if ([Tile.FRAGMENT, Tile.BLOOM].includes(tile)) targets.push({ pos: { x, y }, type: tile }); }));
                spiritsRef.current.filter(s => s.mode === SpiritMode.VULNERABLE).forEach(s => targets.push({ pos: s.pos, type: 'vulnerable_spirit' }));

                const bestPath = pathfindingAI(playerRef.current.pos, targets, predictedThreats, moveHistory);
                
                if (bestPath && bestPath.length > 0) {
                    requestedDirection.current = bestPath[0];
                } else {
                    let bestMove = null;
                    let maxMinDist = -1;
                    const validMoves = [Direction.UP, Direction.DOWN, Direction.LEFT, Direction.RIGHT].filter(dir => !isWall(getNextPosition(playerRef.current.pos, dir)));

                    if (validMoves.length > 0) {
                        for (const move of validMoves) {
                            const nextPos = getNextPosition(playerRef.current.pos, move);
                            let moveScore = Math.min(...predictedThreats.map(t => Math.abs(nextPos.x - t.predictedPos.x) + Math.abs(nextPos.y - t.predictedPos.y)));
                            
                            if (historySize > 0) {
                                const historyIndex = moveHistory.findIndex(h => h.x === nextPos.x && h.y === nextPos.y);
                                if (historyIndex !== -1) {
                                    moveScore -= (historyIndex + 1) * 2;
                                }
                            }

                            if (moveScore > maxMinDist) {
                                maxMinDist = moveScore;
                                bestMove = move;
                            }
                        }
                        requestedDirection.current = bestMove || validMoves[Math.floor(Math.random() * validMoves.length)];
                    } else {
                        requestedDirection.current = Direction.NONE;
                    }
                }
                
                // --- 4. Update History --- (Centralized)
                if (historySize > 0) {
                    moveHistory.push({ ...playerRef.current.pos });
                    if (moveHistory.length > historySize) {
                        moveHistory.shift();
                    }
                } else {
                    moveHistoryRef.current = [];
                }
            }
        }

        // --- 2. Update Player ---
        // Skip updating player logic in Remote Mode (it's updated by RTDB listener)
        if (!isRemoteMode) {
            const p = playerRef.current;
            let nextPosReq = getNextPosition(p.pos, requestedDirection.current);
            if (requestedDirection.current !== Direction.NONE && !isWall(nextPosReq)) {
                p.dir = requestedDirection.current;
                p.pos = nextPosReq;
            } else {
                let nextPosCurrent = getNextPosition(p.pos, p.dir);
                if (!isWall(nextPosCurrent)) p.pos = nextPosCurrent;
            }
        }

        // --- 3. Update Grid & Score ---
        if (!isRemoteMode) { // Only owner updates grid
            const p = playerRef.current;
            const tileAtNewPos = gridRef.current[p.pos.y][p.pos.x];
            if ([Tile.FRAGMENT, Tile.BLOOM, Tile.SPEED_BOOST, Tile.SPIRIT_SLOWDOWN].includes(tileAtNewPos)) {
                if(tileAtNewPos === Tile.FRAGMENT) scoreRef.current += SCORE.FRAGMENT;
                else if(tileAtNewPos === Tile.BLOOM) {
                    scoreRef.current += SCORE.BLOOM;
                    spiritsRef.current.forEach(s => { if (s.mode !== SpiritMode.EATEN) { s.mode = SpiritMode.VULNERABLE; s.vulnerableTimer = Date.now() + POWER_UP_DURATION.SPIRIT_VULNERABLE_MS; } });
                } else if (tileAtNewPos === Tile.SPEED_BOOST) {
                    scoreRef.current += SCORE.POWERUP;
                    playerSpeedRef.current = PLAYER_BOOSTED_SPEED_MS;
                    setTimeout(() => {
                        playerSpeedRef.current = GAME_SPEED_MS;
                    }, POWER_UP_DURATION.SPEED_BOOST_MS);
                } else if (tileAtNewPos === Tile.SPIRIT_SLOWDOWN) {
                    scoreRef.current += SCORE.POWERUP;
                    spiritsSlowedRef.current = true;
                    setTimeout(() => {
                        spiritsSlowedRef.current = false;
                    }, POWER_UP_DURATION.SPIRIT_SLOWDOWN_MS);
                }
                gridRef.current[p.pos.y][p.pos.x] = Tile.EMPTY;
            }
        }

        // --- 4. Update Spirits & Collisions ---
        const newSpirits = spiritsRef.current.map(spirit => {
            if (spiritsSlowedRef.current && renderTick % 2 === 0) {
                return spirit;
            }
            const newSpirit = { ...spirit };
            const oldSpiritPos = { ...newSpirit.pos }; // Store spirit's old position

            // --- Spirit Mode Logic ---
            if (newSpirit.mode === SpiritMode.VULNERABLE && Date.now() > newSpirit.vulnerableTimer) {
                newSpirit.mode = globalSpiritModeRef.current;
            }
            if (newSpirit.mode === SpiritMode.EATEN && newSpirit.pos.x === newSpirit.spawnPoint.x && newSpirit.pos.y === newSpirit.spawnPoint.y) {
                newSpirit.mode = globalSpiritModeRef.current;
            }
            if (newSpirit.mode !== SpiritMode.VULNERABLE && newSpirit.mode !== SpiritMode.EATEN) {
                newSpirit.mode = globalSpiritModeRef.current;
            }

            // --- Target Selection & Movement ---
            let target = null;
            let chosenDir = null;

            switch (newSpirit.mode) {
                case SpiritMode.CHASE:
                    target = playerRef.current.pos; // In Remote Mode, spirits chase the ghost player (visually)
                    break;
                case SpiritMode.SCATTER:
                    target = SPIRIT_SCATTER_TARGETS[newSpirit.id];
                    break;
                case SpiritMode.EATEN:
                    target = newSpirit.spawnPoint;
                    break;
                case SpiritMode.VULNERABLE:
                    const validMoves = [Direction.UP, Direction.DOWN, Direction.LEFT, Direction.RIGHT].filter(dir => !isWall(getNextPosition(newSpirit.pos, dir)));
                    chosenDir = validMoves[Math.floor(Math.random() * validMoves.length)] || Direction.NONE;
                    break;
                default:
                    target = playerRef.current.pos;
            }

            if (target) {
                chosenDir = findNextMoveForSpirit(newSpirit.pos, target, isWall, spiritsRef.current);
            }
            
            if (chosenDir) {
                newSpirit.pos = getNextPosition(newSpirit.pos, chosenDir);
                newSpirit.dir = chosenDir;
            }

            // --- Enhanced Collision Detection ---
            // DISABLE COLLISION IN REMOTE MODE TO PREVENT DESYNC DEATH
            if (!isRemoteMode) {
                const sameTileCollision = playerRef.current.pos.x === newSpirit.pos.x && playerRef.current.pos.y === newSpirit.pos.y;
                const swappedTileCollision = playerRef.current.pos.x === oldSpiritPos.x && playerRef.current.pos.y === oldSpiritPos.y && newSpirit.pos.x === oldPlayerPos.x && newSpirit.pos.y === oldPlayerPos.y;

                if (sameTileCollision || swappedTileCollision) {
                    if (newSpirit.mode === SpiritMode.VULNERABLE) {
                        scoreRef.current += SCORE.SPIRIT;
                        newSpirit.mode = SpiritMode.EATEN;
                    } else if (newSpirit.mode !== SpiritMode.EATEN) {
                        livesRef.current--;
                        if (livesRef.current <= 0) {
                            setGameState(GameState.GAME_OVER);
                        } else {
                            const config = LEVEL_CONFIGS[levelRef.current - 1];
                            playerRef.current = { pos: config.playerStart, dir: Direction.NONE };
                            // Reset spirits to their spawn points and initial mode
                            spiritsRef.current.forEach((s, i) => {
                                const spawnInfo = config.spiritSpawns[i];
                                s.pos = spawnInfo;
                                s.mode = SpiritMode.SCATTER;
                            });
                            // Reset global mode timer as well
                            globalSpiritModeRef.current = SpiritMode.SCATTER;
                            modeTimerRef.current = AI_MODE_DURATION.SCATTER;
                            moveHistoryRef.current = []; // Clear AI history on death
                        }
                    }
                }
            }
            return newSpirit;
        });
        spiritsRef.current = newSpirits;

        gameLogRef.current.push({
            player: playerRef.current,
            grid: gridRef.current,
            spirits: spiritsRef.current,
            timestamp: Date.now(),
        });

        // --- 5. Force Re-render ---
        setRenderTick(tick => tick + 1);
    }, [isSpectatorMode, souls, isWall, gameState, renderTick, isRemoteMode, syncId, myRole, remoteRole]);

    // ... (rest of useEffects and render)
    // --- Setup and Teardown ---
    useEffect(() => {
        if ((isSpectatorMode || isRemoteMode || autoStart) && gameState === GameState.NOT_STARTED) {
            initGame();
        }
    }, [isSpectatorMode, gameState, initGame, isRemoteMode, autoStart]);

    useEffect(() => {
        if (gameState === GameState.PLAYING) {
            lastTickTime.current = performance.now();
            gameLoopId.current = requestAnimationFrame(gameLoop);
        }
        return () => {
            if (gameLoopId.current) cancelAnimationFrame(gameLoopId.current);
        };
    }, [gameState, gameLoop]);

    const handleDirectionChange = (direction) => {
        if (gameState === GameState.PLAYING) {
            requestedDirection.current = direction;
        }
    };

    useEffect(() => {
        if (!isSpectatorMode && !isRemoteMode) {
            const handleKeyDown = (e) => {
                if (gameState !== GameState.PLAYING) return;
                e.preventDefault();
                switch (e.key.toLowerCase()) {
                    case 'arrowup': case 'w': requestedDirection.current = Direction.UP; break;
                    case 'arrowdown': case 's': requestedDirection.current = Direction.DOWN; break;
                    case 'arrowleft': case 'a': requestedDirection.current = Direction.LEFT; break;
                    case 'arrowright': case 'd': requestedDirection.current = Direction.RIGHT; break;
                }
            };
            window.addEventListener('keydown', handleKeyDown);
            return () => window.removeEventListener('keydown', handleKeyDown);
        }
    }, [gameState, isSpectatorMode, isRemoteMode]);

    const renderTile = (tile, x, y) => {
        const key = `${y}-${x}`;
        switch (tile) {
            case Tile.WALL: return <div key={key} className="bg-indigo-900 shadow-[inset_0_0_10px_rgba(167,139,250,0.6)] border border-indigo-700"></div>;
            case Tile.FRAGMENT: return <div key={key} className="flex justify-center items-center"><div className="w-1/3 h-1/3 bg-yellow-300 rounded-full shadow-[0_0_8px_2px_rgba(255,255,0,0.5)]"></div></div>;
            case Tile.BLOOM: return <div key={key} className="flex justify-center items-center"><div className="w-2/3 h-2/3 bg-pink-500 rounded-full shadow-[0_0_10px_4px_rgba(255,105,180,0.7)] animate-pulse"></div></div>;
            case Tile.SPEED_BOOST: return <div key={key} className="flex justify-center items-center text-green-400"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M11.3,2.05C11.54,1.81,11.9,2,11.9,2.33V7h3.33c0.3,0,0.45,0.36,0.25,0.59L8.21,17.95C7.96,18.19,7.6,18,7.6,17.67V13H4.27c-0.3,0-0.45-0.36-0.25-0.59L11.3,2.05z" clipRule="evenodd" /></svg></div>;
            case Tile.SPIRIT_SLOWDOWN: return <div key={key} className="flex justify-center items-center text-cyan-400"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9 5a1 1 0 112 0v4.293l2.146-2.147a1 1 0 111.414 1.414L11 10.414V14a1 1 0 11-2 0V9.586L6.854 11.732a1 1 0 11-1.414-1.414L8 7.707V5a1 1 0 011-1z" clipRule="evenodd" /></svg></div>;
            default: return <div key={key}></div>;
        }
    };

    if (gameState === GameState.NOT_STARTED) {
        return (
            <div className="w-full h-full flex flex-col items-center justify-center text-center p-4">
                <h1 className="text-5xl md:text-7xl font-orbitron text-cyan-400 drop-shadow-[0_0_15px_rgba(0,255,255,0.7)] mb-4">{t('auraEater.startMenu.title')}</h1>
                <p className="text-lg md:text-xl text-indigo-300 mb-8">{t('auraEater.startMenu.subtitle')}</p>
                <button onClick={initGame} className="mt-8 px-8 py-4 bg-cyan-500 text-black font-bold text-2xl rounded-md shadow-[0_0_20px_rgba(0,255,255,0.5)] hover:bg-cyan-400 transition-all transform hover:scale-105">{t('auraEater.startMenu.startButton')}</button>
            </div>
        );
    }

    return (
        <div className="w-full h-full flex flex-col items-center justify-center p-2" ref={gameContainerRef}>
            <div className="flex justify-between items-center w-full text-base md:text-lg font-orbitron text-cyan-300 mb-2 md:mb-4 px-4 max-w-2xl mx-auto">
                <div>{t('auraEater.hud.score')} <span className="text-white">{scoreRef.current}</span></div>
                {isSpectatorMode && <div>{t('auraEater.hud.souls')} <span className="text-white">{souls}</span></div>}
                <div>{t('auraEater.hud.lives')} <span className="text-white">{livesRef.current}</span></div>
            </div>
            <div className="relative max-w-2xl w-full mx-auto">
                {gameState === GameState.GAME_OVER && (
                    <GameOverMenu 
                        score={finalScore} 
                        soulsGained={soulsGained}
                        previousSouls={previousSoulsRef.current}
                        onRestart={survivalMode ? () => {} : initGame} 
                    />
                )}
                <div className="bg-black border-2 border-indigo-700 shadow-[0_0_20px_rgba(79,70,229,0.5)]" style={{ width: GRID_WIDTH * tileSize, height: GRID_HEIGHT * tileSize }}>
                    <div className="grid" style={{ gridTemplateColumns: `repeat(${GRID_WIDTH}, 1fr)`, width: '100%', height: '100%' }}>
                        {gridRef.current.map((row, y) => row.map((tile, x) => renderTile(tile, x, y)))}
                    </div>
                    <div className="absolute transition-all duration-100 ease-linear" style={{ left: playerRef.current.pos.x * tileSize, top: playerRef.current.pos.y * tileSize, width: tileSize, height: tileSize }}><PlayerIcon isBoosted={playerSpeedRef.current !== GAME_SPEED_MS} /></div>
                    {spiritsRef.current.map(spirit => (<div key={spirit.id} className="absolute transition-all duration-100 ease-linear" style={{ left: spirit.pos.x * tileSize, top: spirit.pos.y * tileSize, width: tileSize, height: tileSize }}><SpiritIcon spirit={spirit} isSlowed={spiritsSlowedRef.current}/></div>))}
                </div>
            </div>
            {!isSpectatorMode && !isRemoteMode && gameState === GameState.PLAYING && <MobileControls onDirectionChange={handleDirectionChange} />}
        </div>
    );
};

export default AuraEater;
